import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  LogIn,
  Check,
  X,
  FileText,
  ClipboardList,
  ShieldCheck,
  ChevronDown,
  Loader2,
  CreditCard,
  Users,
  Play,
  MessageCircle,
} from 'lucide-react';
import { trackInitiateCheckout, trackViewContent, trackContact } from '@/lib/metaPixel';
import {
  CURRENT_PRICE_ARS,
  PREVIOUS_PRICE_ARS,
  DISCOUNT_PERCENT,
  PACK_PRICE_ARS,
  PACK_ENTREVISTAS,
  GIMNASIO_PRECIOS,
  formatPriceARS,
} from '@/config/pricing';
import LeadModal from '@/components/LeadModal';
import {
  FUENTE_TITULOS,
  FUENTE_ROTULO,
  FUENTE_TEXTO,
  RESALTADO,
  BTN_PRIMARY,
  BTN_SECUNDARIO,
  Papel,
  Mancha,
  GRANO,
} from '@/components/Collage';

// ---------------------------------------------------------------------------
// Landing EVALPRO. El copy vive en LANDING-COPY.md (raiz del repo); si se
// cambia un texto aca, cambiarlo alla tambien.
//
// Estructura: 6 bloques. Navy para hero, precio y footer; blanco o gris muy
// claro para las secciones de lectura. Antes era navy de punta a punta:
// cansaba leerlo y chocaba con la app, que es clara.
//
// Precios: NUNCA escritos a mano en este archivo. Salen de config/pricing, que
// tiene que coincidir con lo que cobra el backend (EVALPRO_PRICE_ARS en
// server.py). Si la landing muestra un precio y el checkout cobra otro, es
// publicidad enganosa.
// ---------------------------------------------------------------------------

// Estilo collage de recortes (28/10/2026, ver ESTILO-COLLAGE.md): fondo negro
// con grano, papel rasgado para el texto, mancha verde con trama de puntos.
// Tokens: negro #0b0b0c, crema #efe6d2 (títulos), papel #d9cfb6, tinta #14110d
// (texto sobre papel), verde #27AE60 (acento fuerte) y #8CF0B4 (acento claro,
// para letras sobre negro: el verde puro sobre negro no se lee).
const C = {
  navy: '#0b0b0c',
  navyDeep: '#070708',
  paper: '#121214',
  white: '#0b0b0c',
};


// Video del hero. Es el unico de la landing: la demo larga de YouTube no va,
// porque para convencer ya esta la demo jugable ("Probalo"), que es mas fuerte
// que mirar un video.
const VIDEO_INTRO_ID = '4Yz_P-xszX8'; // "Te cuento que es EVALPRO", ~2 min

// Contacto B2B. Numero de WhatsApp Business en formato internacional
// (54 + 9 + caracteristica sin 0 + numero sin 15).
const WHATSAPP_NUMERO = '5492216252996';
const WHATSAPP_MENSAJE =
  'Hola Fede, tengo un equipo de profesionales y quiero saber cómo funciona EVALPRO para instituciones.';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(WHATSAPP_MENSAJE)}`;
const INSTAGRAM_URL = 'https://instagram.com/ejerciciofisicoysaludok';

// Sección #gimnasios: destino de los anuncios B2B. La oferta es capacitación
// del equipo + acceso a EVALPRO para todos, con precio según el tamaño del
// equipo (se arma por WhatsApp o Instagram).
const GIMNASIOS = [
  {
    Icon: Users,
    titulo: 'Capacitación para tus entrenadores',
    desc: 'En vivo y con casos reales: cómo hacer la entrevista inicial y qué hacer con cada escenario.',
  },
  {
    Icon: ClipboardList,
    titulo: 'Una cuenta de EVALPRO para el gimnasio',
    desc: 'Todo el equipo trabaja en la misma cuenta, con las entrevistas ordenadas por grupos.',
  },
  {
    Icon: FileText,
    titulo: 'Cada alumno nuevo, registrado',
    desc: 'Su entrevista y su informe quedan guardados en su ficha.',
  },
];

const PROBLEMAS = [
  'Algunas por WhatsApp, otras en la primera sesión, otras nunca.',
  'Un PAR-Q fotocopiado, o un formulario que armaste vos y fuiste cambiando.',
  'La decisión de "este arranca suave" vive en tu cabeza, no en un papel.',
  'Y cada tanto se te cuela algo que tendrías que haber preguntado.',
];

const TRES_DATOS = [
  { titulo: 'Actividad física actual', desc: '¿Es activo regular o no?' },
  { titulo: 'Enfermedad cardiometabólica', desc: '¿Tiene un diagnóstico declarado?' },
  { titulo: 'Signos o síntomas', desc: '¿Hay algo que amerite consulta antes de empezar?' },
];

// Mismos nombres que usa el motor (decision_presentation.py). Si cambian alla,
// cambiarlos aca: el profesional los tiene que reconocer en su informe.
const ESCENARIOS = [
  { color: '#8CF0B4', nombre: 'Inicio habilitado', desc: 'Puede iniciar sin restricción.' },
  { color: '#F4C430', nombre: 'Inicio prudente', desc: 'Inicia y progresa de a poco hasta intensidad vigorosa.' },
  { color: '#D97706', nombre: 'Continúa con seguimiento', desc: 'Sigue con lo que hace. Consulta médica antes de pasar a vigoroso.' },
  { color: '#EA580C', nombre: 'Consulta médica recomendada', desc: 'Consulta antes de iniciar. Después, progresión gradual.' },
  { color: '#C83A2A', nombre: 'Requiere evaluación médica', desc: 'Se recomienda no iniciar hasta que lo vea un médico.' },
];

const NO_HACE = [
  'No reemplaza tu criterio profesional',
  'No reemplaza el apto médico',
  'No diagnostica ni es una evaluación funcional',
];

// Corta a propósito: el detalle del informe ya está en "Qué trae el informe" y
// la Ley 25.326 en el pie. Cuatro ítems se leen; nueve no.
const INCLUYE = [
  'Entrevistas ilimitadas, cada una con su informe',
  'Ficha de cada alumno, con retest y seguimiento',
  'Link para que el alumno la complete desde el celular',
  'Guía de uso escrita',
];

// Testimonios reales, con el permiso de cada uno. Se publican tal como los
// aprobaron: no editar el texto sin volver a pedir el OK.
const TESTIMONIOS = [
  {
    texto:
      'Comencé a usarlo con mis profes, en el gimnasio con seguimiento médico. Me encanta esta nueva herramienta. En el protocolo de evaluación hacemos primero EVALPRO y después las evaluaciones funcionales, y la gente se va encantada.',
    nombre: 'Gabriela',
    rol: 'Médica fisiatra y deportóloga · Clínica Puelche',
  },
  {
    texto: 'La app es excelente para recibir alumnos.',
    nombre: 'Gimnasio FITON',
    rol: 'Gimnasio',
  },
  {
    texto:
      'La verdad tremenda la herramienta, estoy más que contento. Ya estoy palpitando el momento en el que la voy a usar, y sé que va a ser gratificante haber confiado en vos y tu producto.',
    nombre: 'Marcos Buenaventura',
    rol: 'Profesor',
  },
];

// Cómo funciona: tres pasos en un solo recuadro, sin capturas (05/10/2026:
// las imágenes alargaban mucho la página en el celular). Lo central es la
// entrevista que hace el profesional; el link para el alumno es una opción.
const PASOS = [
  {
    titulo: 'Hacés la entrevista guiada',
    desc: 'Con la persona adelante, paso a paso. Cada pregunta trae su explicación.',
  },
  {
    titulo: 'Ves cómo avanzar',
    desc: 'La conducta sugerida para ese alumno, con el porqué y sus zonas de frecuencia cardíaca.',
  },
  {
    titulo: 'Queda el registro',
    desc: 'Un informe con todo lo que relevaste, guardado en la ficha del alumno.',
  },
];

const FAQ = [
  {
    q: '¿Reemplaza mi criterio profesional?',
    a: 'No. Te da la información ordenada y una conducta sugerida. La decisión la tomás vos.',
  },
  {
    q: '¿Necesito conocimientos médicos?',
    a: 'No. El sistema te guía las preguntas y te traduce el resultado a conducta operativa.',
  },
  {
    q: '¿Es lo mismo que un PAR-Q?',
    a: 'No. El PAR-Q te dice sí o no. EVALPRO te dice en cuál de cinco escenarios está la persona y qué hacer en cada uno.',
  },
  {
    q: '¿Funciona en el celular?',
    a: 'Sí. Y tu alumno puede cargar sus datos desde el suyo, con un link que le mandás.',
  },
  {
    q: '¿Qué pasa después de que compro?',
    a: 'Te llega el acceso por mail al instante, y la invitación a la próxima capacitación en vivo.',
  },
  {
    q: '¿Me conviene si tengo pocos alumnos?',
    a: `Para eso está el pack: ${PACK_ENTREVISTAS} entrevistas por ${formatPriceARS(PACK_PRICE_ARS)}, que no vencen. Si después pasás al ilimitado, te descontamos lo que pagaste en packs.`,
  },
  {
    q: '¿Y si no me sirve?',
    a: 'Tenés 10 días para pedir la devolución.',
  },
];

// ---------------- Piezas de layout ----------------

const TONOS = {
  navy: { bg: C.navy, oscuro: true },
  deep: { bg: C.navyDeep, oscuro: true },
  paper: { bg: C.paper, oscuro: true },
  white: { bg: C.white, oscuro: true },
};

function Section({ tono = 'navy', id, angosta = false, className = '', children, 'data-testid': testId }) {
  return (
    <section
      id={id}
      className={`w-full px-4 py-16 sm:py-20 scroll-mt-20 ${className}`}
      style={{ backgroundColor: TONOS[tono].bg, backgroundImage: GRANO }}
      data-testid={testId}
    >
      <div className={`${angosta ? 'max-w-3xl' : 'max-w-5xl'} mx-auto`}>{children}</div>
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <p className={`${FUENTE_ROTULO} text-sm mb-3 text-[#8CF0B4] [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]`}>
      {children}
    </p>
  );
}

function Titulo({ className = '', children }) {
  return (
    <h2 className={`${FUENTE_TITULOS} text-3xl sm:text-4xl lg:text-5xl leading-[1.02] text-[#efe6d2] ${className}`}>
      {children}
    </h2>
  );
}

function Acento({ children }) {
  return <span className={RESALTADO.replace('4px', '3px')}>{children}</span>;
}

function GuiasBar() {
  return (
    <div className="flex flex-col items-center gap-3" data-testid="landing-guidelines-bar">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#a9a08c]">
        Basado en guías internacionales de ejercicio y salud
      </p>
      <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3">
        {['ACSM', 'AHA', 'ESC', 'OMS'].map((g) => (
          <span
            key={g}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-[#8CF0B4]/10 border border-[#8CF0B4]/30 text-[#8CF0B4]"
          >
            {g}
          </span>
        ))}
      </div>
    </div>
  );
}

// Video del hero con "fachada": se muestra la foto de Fede con un boton de
// play, y el iframe de YouTube recien se carga cuando alguien toca. El iframe
// pesa cientos de KB de scripts; cargarlo de entrada frena la pagina en el
// celular, que es de donde viene casi todo el trafico de los anuncios.
function VideoIntro() {
  const [reproduciendo, setReproduciendo] = useState(false);
  return (
    <div
      className="relative w-full aspect-[4/3] sm:aspect-video rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-white/10 bg-black"
      data-testid="landing-hero-video"
    >
      {reproduciendo ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${VIDEO_INTRO_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title="Te cuento qué es EVALPRO"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setReproduciendo(true)}
          className="group absolute inset-0 w-full h-full"
          aria-label="Reproducir video: Te cuento qué es EVALPRO, 2 minutos"
          data-testid="landing-hero-video-play"
        >
          <img
            src="/landing/hero-fede-laptop.jpg"
            alt="Federico Silvero usando EVALPRO en un gimnasio"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c]/90 via-[#0b0b0c]/20 to-transparent" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-[#8CF0B4] shadow-[0_0_16px_rgba(140,240,180,0.22)] transition-transform group-hover:scale-110">
              <Play className="h-7 w-7 sm:h-8 sm:w-8 text-[#0b0b0c] ml-1" fill="currentColor" />
            </span>
          </span>
          <span className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 text-left">
            <span className="block text-white font-semibold text-sm sm:text-base">
              Te cuento qué es EVALPRO
            </span>
            <span className="block text-[#d9cfb6] text-xs sm:text-sm">2 minutos</span>
          </span>
        </button>
      )}
    </div>
  );
}

function FAQAccordion() {
  const [abierta, setAbierta] = useState(null);
  return (
    <div className="space-y-3" data-testid="landing-faq-list">
      {FAQ.map((item, i) => {
        const open = abierta === i;
        return (
          <div
            key={item.q}
            className={`bg-white/[0.04] border rounded-2xl overflow-hidden transition-colors ${
              open ? 'border-[#8CF0B4]/50' : 'border-white/10 hover:border-white/25'
            }`}
            data-testid={`faq-item-${i}`}
          >
            <button
              type="button"
              onClick={() => setAbierta(open ? null : i)}
              className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 sm:px-6"
              aria-expanded={open}
              data-testid={`faq-toggle-${i}`}
            >
              <span className="text-white font-semibold text-sm sm:text-base leading-snug">
                {item.q}
              </span>
              <ChevronDown
                className={`h-4 w-4 flex-shrink-0 text-[#a9a08c] transition-transform duration-200 ${
                  open ? 'rotate-180 text-[#8CF0B4]' : ''
                }`}
              />
            </button>
            <div
              className={`grid transition-all duration-200 ease-out ${
                open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <p className="px-5 pb-5 sm:px-6 text-[#d9cfb6] text-sm leading-relaxed">{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Pagina ----------------

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);

  const cuota = formatPriceARS(Math.round(CURRENT_PRICE_ARS / 3));

  // Meta Pixel: ViewContent una vez al montar.
  useEffect(() => {
    const id = setTimeout(() => {
      try { trackViewContent(); } catch (_) { /* noop */ }
    }, 300);
    return () => clearTimeout(id);
  }, []);

  // Deep-link a secciones por hash (#prueba, #acceso-unico, #gimnasios, #guia).
  // Todos se usan desde afuera: #prueba desde los anuncios (y la ruta /prueba),
  // #acceso-unico desde los mails del backend, #gimnasios desde los anuncios
  // para gimnasios, #guia desde Instagram.
  // React Router no dispara el scroll nativo al hash, asi que se fuerza. Se
  // repite unas veces en los primeros segundos: las imagenes de arriba
  // terminan de cargar despues y empujan la seccion hacia abajo (entrando
  // desde un anuncio, quedaba a mitad de camino). Si la persona ya empezo a
  // moverse, no se la mueve mas.
  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.replace('#', '');
    let seMovio = false;
    const marcar = () => { seMovio = true; };
    const ir = () => {
      if (seMovio) return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    };
    const eventos = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    eventos.forEach((e) => window.addEventListener(e, marcar, { passive: true }));
    const timers = [0, 400, 1200, 2500].map((ms) => setTimeout(ir, ms));
    return () => {
      timers.forEach(clearTimeout);
      eventos.forEach((e) => window.removeEventListener(e, marcar));
    };
  }, [location.hash]);

  const handleActivate = () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      trackInitiateCheckout({ content_name: 'EVALPRO ilimitado', content_type: 'product', content_ids: ['ilimitado'] });
    } catch (err) {
      console.error('[Pixel InitiateCheckout]', err);
    }
    navigate('/checkout');
  };

  // El boton del hero va directo al checkout del pack (31/10/2026): bajar al
  // bloque de precio mostraba tres opciones en el primer click y confundia.
  // Antes el hero bajaba al precio porque, cuando iba directo al checkout del
  // ilimitado, se perdia el 92%. Esto es otro producto y otro precio: medir el
  // abandono del checkout del pack las primeras semanas.

  const handleProbar = () => navigate('/evaluate');

  // Pack de entrevistas: va directo al checkout con el producto elegido. El
  // precio que se cobra lo calcula el servidor, no esta pagina.
  const handlePack = () => {
    try {
      trackInitiateCheckout({ content_name: `Pack de ${PACK_ENTREVISTAS} entrevistas`, value: PACK_PRICE_ARS, content_type: 'product', content_ids: ['pack'] });
    } catch (_) { /* noop */ }
    navigate('/checkout?producto=pack');
  };

  const handleWhatsApp = () => {
    try { trackContact({ contact_channel: 'whatsapp' }); } catch (_) { /* noop */ }
  };
  const handleInstagram = () => {
    try { trackContact({ contact_channel: 'instagram' }); } catch (_) { /* noop */ }
  };

  return (
    <div
      className="min-h-screen min-h-svh flex flex-col overflow-x-hidden w-full"
      style={{ backgroundColor: C.navy, fontFamily: FUENTE_TEXTO }}
    >
      {/* ============ BARRA SUPERIOR ============ */}
      {/* Fija arriba: "Iniciar sesión" se ve siempre, también en el celular.
          Los logos son versiones claras de los originales (los originales son
          azul oscuro y sobre este fondo no se veían). */}
      <header
        className="sticky top-0 z-40 w-full border-b border-white/10 backdrop-blur"
        style={{ backgroundColor: 'rgba(11, 11, 12, 0.85)' }}
      >
        <div className="px-4 h-16 sm:h-20 flex items-center justify-between gap-3 max-w-6xl mx-auto w-full">
          <div className="flex items-center gap-4 min-w-0">
            <img
              src="/landing/logo-evalpro-claro.png"
              alt="EVALPRO"
              className="h-9 sm:h-11 w-auto block"
              data-testid="landing-header-logo"
            />
            <span className="hidden sm:block h-9 w-px bg-white/15" aria-hidden="true" />
            <img
              src="/landing/logo-efs-claro.png"
              alt="Ejercicio Físico & Salud"
              className="hidden sm:block h-11 w-auto"
            />
          </div>
          <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
          <a
            href="#gimnasios"
            className="hidden md:inline text-sm font-semibold text-[#efe6d2] hover:text-[#8CF0B4] transition-colors"
            data-testid="landing-header-gimnasios"
          >
            Para gimnasios
          </a>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="flex-shrink-0 inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full border-2 border-white/40 text-white text-sm font-semibold hover:bg-[#8CF0B4] hover:text-[#0b0b0c] hover:border-[#8CF0B4] transition-colors"
            data-testid="landing-login-btn"
          >
            <LogIn className="h-4 w-4" />
            Iniciar sesión
          </button>
          </div>
        </div>
      </header>

      {/* ============ 1 · HERO ============ */}
      {/* Es el anuncio de Meta hecho página (28-29/10/2026): mismo gancho, mismos
          tres rótulos, mismas tres tarjetas y el pack. Ancla #prueba: los
          anuncios y la ruta /prueba entran por acá. El botón del pack va directo
          al checkout del pack (ver handlePack). */}
      <section id="prueba" className="relative isolate overflow-hidden px-4 pt-10 sm:pt-16 pb-14 scroll-mt-24" data-testid="landing-hero">
        {/* Foto en gris de fondo, grano y viñeta */}
        <img
          src="/landing/hero-fede-laptop.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 w-full h-full object-cover opacity-[0.2] [filter:grayscale(1)_contrast(1.5)]"
        />
        <div className="absolute inset-0 -z-10" style={{ backgroundImage: GRANO }} aria-hidden="true" />
        <div
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle,rgba(255,255,255,0.07)_1px,transparent_1.5px)] [background-size:7px_7px]"
          aria-hidden="true"
        />

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,11fr)_minmax(0,9fr)] gap-10 lg:gap-14 items-center">
          <div className="text-center lg:text-left">
            <p className={`${FUENTE_ROTULO} text-sm sm:text-base text-[#8CF0B4] [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]`}>
              Para profes y gimnasios
            </p>
            <h1
              className={`${FUENTE_TITULOS} mt-3 text-[#efe6d2] leading-[1.04] break-words [text-shadow:0_3px_0_rgba(0,0,0,0.85)]`}
              style={{ fontSize: 'clamp(2.1rem, 5.6vw, 3.5rem)' }}
              data-testid="landing-title"
            >
              Lo fundamental de <span className="whitespace-nowrap">tu alumno,</span>
              <br />
              <span className={RESALTADO}>¿está en el apto médico?</span>
            </h1>

            {/* Los tres datos del algoritmo, como rótulos */}
            <ul
              className="mt-6 flex flex-wrap justify-center lg:justify-start gap-2.5"
              data-testid="landing-method-callout"
              style={{ flexWrap: 'wrap' }}
            >
              {['Signos y síntomas', 'Enfermedades', 'Nivel de actividad habitual'].map((t) => (
                <li
                  key={t}
                  className={`${FUENTE_TITULOS} border-2 border-[#efe6d2] bg-black/70 px-3 py-1.5 text-sm sm:text-base text-[#efe6d2]`}
                >
                  {t}
                </li>
              ))}
            </ul>

            {/* Mancha verde con las tres tarjetas y el pack */}
            <div className="relative mt-8 max-w-xl mx-auto lg:mx-0">
              <Mancha className="-inset-x-4 -inset-y-5 sm:-inset-x-6 sm:-inset-y-7" />
              <div className="relative grid gap-2.5 py-1">
                {[
                  'EVALPRO te guía la entrevista inicial, pregunta por pregunta.',
                  'Informe en PDF con la conducta sugerida.',
                  'Conocés el perfil clínico básico de tu alumno, con lo indispensable.',
                ].map((t, i) => (
                  <Papel key={t} semilla={i + 2} giro={i % 2 ? 0.5 : -0.5} className="px-4 py-3 sm:px-5">
                    <div className="flex items-center gap-3 text-left" style={{ flexWrap: 'nowrap' }}>
                      <span
                        className={`${FUENTE_TITULOS} flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center bg-[#27AE60] border-2 border-[#14110d] text-xl text-[#14110d]`}
                      >
                        {i + 1}
                      </span>
                      <p className={`${FUENTE_TITULOS} text-base sm:text-xl leading-tight text-[#14110d]`}>{t}</p>
                    </div>
                  </Papel>
                ))}
                <Papel semilla={9} giro={-0.4} className="px-4 py-4 sm:px-5" data-testid="hero-pack">
                  <p className={`${FUENTE_TITULOS} text-xl sm:text-3xl leading-tight text-[#14110d] text-left`}>
                    Probalo con {PACK_ENTREVISTAS} alumnos por {formatPriceARS(PACK_PRICE_ARS)}
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-3" style={{ flexWrap: 'nowrap' }}>
                    <Button size="lg" onClick={handlePack} data-testid="landing-buy-btn" className={`${BTN_PRIMARY} h-12 px-4 sm:px-5 text-xs sm:text-base whitespace-nowrap`}>
                      Comprar el pack
                      <ArrowRight className="hidden sm:block h-4 w-4 ml-2" />
                    </Button>
                    <img src="/landing/logo-evalpro-papel.png" alt="EVALPRO" className="h-8 sm:h-10 w-auto flex-shrink-0" />
                  </div>
                </Papel>
              </div>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button
                size="lg"
                onClick={handleProbar}
                data-testid="landing-hero-demo-btn"
                className={`w-full sm:w-auto ${BTN_SECUNDARIO}`}
              >
                Probar la entrevista gratis
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 justify-center lg:justify-start text-xs sm:text-sm text-[#d9cfb6] [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]">
              <span className="flex items-center gap-1.5" data-testid="hero-price-chip">
                <CreditCard className="h-4 w-4 text-[#8CF0B4]" />
                EVALPRO ilimitado: {formatPriceARS(CURRENT_PRICE_ARS)} o 3 cuotas de {cuota}
              </span>
              <span className="flex items-center gap-1.5" data-testid="landing-proof-chip">
                <Users className="h-4 w-4 text-[#8CF0B4]" />
                Profes, centros de entrenamiento, gimnasios y clínicas de todo el país ya lo usan
              </span>
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-[#8CF0B4]" />
                Garantía 10 días
              </span>
            </div>
          </div>

          <div className="w-full max-w-xl mx-auto">
            <VideoIntro />
            <p className={`${FUENTE_ROTULO} mt-3 text-center text-[#d9cfb6] text-[11px] sm:text-xs leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.9)]`}>
              Basado en el algoritmo de screening del Colegio Americano de Medicina del Deporte.
              Sugerencia de screening. No reemplaza al médico.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mt-12 pt-7 border-t border-white/10">
          <GuiasBar />
        </div>
      </section>

      {/* ============ LO QUE DICEN ============ */}
      {/* Prueba de terceros, justo después del hero: era lo que más le faltaba
          a la landing. */}
      <section className="w-full px-4 py-12 sm:py-14" style={{ backgroundColor: C.navyDeep }} data-testid="landing-testimonials">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8CF0B4] text-center">Lo que dicen</p>
          {/* En el celular se deslizan de costado: tres tarjetas una abajo de la
              otra eran casi una pantalla y media. flexWrap va en línea porque
              index.css fuerza wrap en todos los .flex del celular. */}
          <div
            className="mt-6 -mx-4 px-4 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 md:mx-0 md:px-0 md:grid md:grid-cols-3 md:overflow-visible"
            style={{ flexWrap: 'nowrap' }}
          >
            {TESTIMONIOS.map((t, i) => (
              <div key={t.nombre} className="flex-shrink-0 w-[85%] md:w-auto snap-start py-2">
                <Papel semilla={20 + i} giro={i % 2 ? 0.6 : -0.6} className="h-full p-6 sm:p-7">
                  <figure>
                    <span
                      className={`${FUENTE_TITULOS} absolute top-0 left-4 text-7xl leading-none text-[#27AE60]`}
                      aria-hidden="true"
                    >
                      “
                    </span>
                    <blockquote className="relative pt-4 text-[#14110d] text-base sm:text-lg leading-relaxed">
                      {t.texto}
                    </blockquote>
                    <figcaption className="mt-4">
                      <p className={`${FUENTE_ROTULO} text-sm text-[#14110d]`}>{t.nombre}</p>
                      <p className="text-[#4a4333] text-sm">{t.rol}</p>
                    </figcaption>
                  </figure>
                </Papel>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 2 · EL PROBLEMA ============ */}
      <Section tono="white" data-testid="landing-problem-section">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 lg:gap-12 items-start">
          <div>
            <Eyebrow>El problema</Eyebrow>
            <Titulo>
              Tu entrevista inicial existe. <Acento>Lo que no existe es un protocolo.</Acento>
            </Titulo>
            <p className="mt-4 text-[#d9cfb6] text-base sm:text-lg">
              Las preguntas las hacés. Pero no siempre las mismas, ni con el mismo criterio, ni
              quedan en el mismo lugar.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="landing-problem">
            {PROBLEMAS.map((t) => (
              <li
                key={t}
                className="flex items-start gap-3 bg-white/[0.04] border-l-4 border-[#C83A2A]/70 rounded-r-xl px-4 py-3 text-[#efe6d2] text-sm sm:text-base leading-relaxed"
              >
                <X className="h-4 w-4 text-[#FF6B5A] mt-1 flex-shrink-0" strokeWidth={3} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* ============ 3 · EL MÉTODO ============ */}
      <Section tono="paper" data-testid="landing-method-section">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>El método</Eyebrow>
          <Titulo>
            Tres datos, cinco escenarios, <Acento>una conducta para cada uno.</Acento>
          </Titulo>
          <p className="mt-4 text-[#d9cfb6] text-base sm:text-lg leading-relaxed">
            EVALPRO te guía la entrevista paso a paso y al final te dice qué hacer. No te lo
            impone: te lo sugiere, con el fundamento a la vista.
          </p>
        </div>

        {/* Compacto desde el 05/10/2026: en el celular eran once tarjetas. */}
        <div className="mt-8 max-w-4xl mx-auto bg-white/[0.04] border border-white/10 rounded-3xl p-5 sm:p-7 grid gap-6">
          {/* Los 3 datos */}
          <ol className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="landing-three-data">
            {TRES_DATOS.map(({ titulo, desc }, i) => (
              <li key={titulo} className="flex items-start gap-3" style={{ flexWrap: 'nowrap' }}>
                <span className={`${FUENTE_TITULOS} text-3xl  leading-none text-[#8CF0B4]`}>{i + 1}</span>
                <div className="min-w-0">
                  <p className="font-semibold text-white leading-snug">{titulo}</p>
                  <p className="text-[#a9a08c] text-sm mt-0.5">{desc}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* Los 5 escenarios, como un semáforo: una fila por escenario */}
          <div className="pt-5 border-t border-white/10" data-testid="landing-engine-infographic">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a9a08c] mb-3">
              Los 5 escenarios posibles
            </p>
            <ol className="grid gap-2.5">
              {ESCENARIOS.map(({ color, nombre, desc }) => (
                <li key={nombre} className="flex items-start gap-3" style={{ flexWrap: 'nowrap' }}>
                  <span
                    className="mt-1.5 h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }}
                    aria-hidden="true"
                  />
                  <p className="text-sm leading-snug min-w-0">
                    <span className="font-semibold text-white">{nombre}.</span>{' '}
                    <span className="text-[#a9a08c]">{desc}</span>
                  </p>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-[#a9a08c] text-xs leading-relaxed">
              Los síntomas se miran primero: si aparece uno, la recomendación es consultar al médico
              antes de empezar. Si no, el sistema cruza actividad y enfermedad.
            </p>
          </div>

          {/* Alcance: tiene que verse que no reemplaza al médico. */}
          <div className="pt-5 border-t border-white/10" data-testid="landing-scope">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF6B5A] mb-2">Qué no hace</p>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {NO_HACE.map((t) => (
                <li key={t} className="flex items-start gap-2 text-[#efe6d2] text-sm" style={{ flexWrap: 'nowrap' }}>
                  <X className="h-4 w-4 text-[#FF6B5A] mt-0.5 flex-shrink-0" strokeWidth={3} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-[#a9a08c]">
          Basado en el algoritmo de screening preparticipativo del ACSM (Riebe et al., 2015), AHA y ESC.
        </p>
      </Section>

      {/* ============ 3 bis · CÓMO FUNCIONA ============ */}
      {/* Un solo recuadro compacto: tres pasos y la opción del link. */}
      <Section tono="white" data-testid="landing-how-section">
        <div className="max-w-4xl mx-auto bg-white/[0.04] border border-white/10 rounded-3xl p-6 sm:p-8">
          <Eyebrow>Cómo funciona</Eyebrow>
          <p className={`${FUENTE_TITULOS} text-2xl sm:text-3xl  leading-[0.95] text-white`}>
            Vos hacés la entrevista. <Acento>EVALPRO te sugiere cómo avanzar.</Acento>
          </p>
          <ol className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {PASOS.map((paso, i) => (
              <li key={paso.titulo} className="flex items-start gap-3" style={{ flexWrap: 'nowrap' }}>
                <span className={`${FUENTE_TITULOS} text-3xl  leading-none text-[#8CF0B4]`}>{i + 1}</span>
                <div className="min-w-0">
                  <p className="font-semibold text-white leading-snug">{paso.titulo}</p>
                  <p className="text-[#a9a08c] text-sm mt-1 leading-relaxed">{paso.desc}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-6 pt-5 border-t border-white/10 text-[#d9cfb6] text-sm leading-relaxed" data-testid="landing-how-optional">
            <span className="font-semibold text-[#8CF0B4]">Opcional:</span> le mandás un link por
            WhatsApp y la persona completa sus datos desde el celular antes de llegar. No ve ningún
            resultado: te llega a vos, revisás y decidís.
          </p>
        </div>
      </Section>

      {/* ============ 4 · QUIÉN ESTÁ DETRÁS ============ */}
      <Section tono="paper" data-testid="landing-founder-section">
        <div className="grid grid-cols-1 sm:grid-cols-[12rem_minmax(0,1fr)] md:grid-cols-[15rem_minmax(0,1fr)] gap-6 md:gap-10 items-center">
          <img
            src="/landing/fede-retrato.jpg"
            alt="Federico Silvero, creador de EVALPRO"
            className="mx-auto w-48 sm:w-full h-auto rounded-2xl shadow-xl shadow-black/50 object-cover"
            loading="lazy"
            data-testid="landing-creator-photo"
          />
          <div data-testid="landing-creator-copy">
            <Eyebrow>Quién está detrás</Eyebrow>
            <Titulo>Federico Silvero</Titulo>
            <div className="mt-4 space-y-3 text-[#efe6d2] text-base leading-relaxed">
              <p>
                Profesor Nacional de Educación Física, con posgrado en Prevención y Readaptación a
                través del Ejercicio (Facultad de Ciencias Médicas, UNLP). Hace doce años que
                trabajo en el cruce entre ejercicio y salud, y hoy lo hago dentro de un hospital
                de La Plata.
              </p>
              <p>
                EVALPRO salió de algo que aprendí con los años: lo que define cómo programás no es
                el plan, es lo que averiguaste antes de escribirlo. Y esa parte casi todos la
                hacemos con tres preguntas sueltas y una ficha que queda archivada.{' '}
                <span className="font-semibold text-white">Yo incluido.</span>
              </p>
            </div>
            <img
              src="/landing/logo-efs-claro.png"
              alt="Ejercicio Físico & Salud"
              className="mt-5 h-14 w-auto"
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* ============ 5 · PRECIO ============ */}
      {/* Tres opciones: pack (entrada), ilimitado (el del medio, destacado) y
          gimnasios (29/10/2026). La de gimnasios es la capacitación B2B real,
          con su precio de lista: al lado de ella el ilimitado queda como la
          opción razonable para un profe solo. En el celular va primero el
          ilimitado. */}
      <Section tono="navy" id="acceso-unico" data-testid="landing-price-section">
        <div className="text-center" data-testid="landing-price-block">
          <Eyebrow>Acceso</Eyebrow>
          <Titulo>
            Un pago. <Acento>Sin suscripción.</Acento>
          </Titulo>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
          {/* Ilimitado */}
          <div className="relative lg:order-2 bg-white/[0.05] border-2 border-[#8CF0B4]/60 rounded-3xl p-6 sm:p-8 shadow-[0_0_24px_rgba(140,240,180,0.12)]">
            <span className="absolute -top-3 left-6 bg-[#8CF0B4] text-[#0b0b0c] text-[11px]  uppercase tracking-[0.14em] rounded-full px-3 py-1">
              El más elegido
            </span>
            <p className={`${FUENTE_TITULOS} text-white text-2xl `}>EVALPRO ilimitado</p>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className={`${FUENTE_TITULOS} text-6xl sm:text-7xl lg:text-6xl text-white leading-none`}>
                {formatPriceARS(CURRENT_PRICE_ARS)}
              </span>
              <span className="text-[#a9a08c] line-through text-base">{formatPriceARS(PREVIOUS_PRICE_ARS)}</span>
              <span className="bg-[#8CF0B4]/15 text-[#8CF0B4] text-xs  rounded-full px-2.5 py-0.5">
                {DISCOUNT_PERCENT}% OFF
              </span>
            </div>
            <p
              className="mt-2 inline-flex items-center gap-2 text-[#8CF0B4] text-sm font-semibold"
              data-testid="landing-installments-chip"
            >
              <CreditCard className="h-4 w-4" />
              o 3 cuotas sin interés de {cuota}
            </p>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-5 gap-y-2" data-testid="landing-includes">
              {INCLUYE.map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-[#efe6d2]">
                  <Check className="h-4 w-4 text-[#8CF0B4] flex-shrink-0 mt-0.5" strokeWidth={3} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            {/* Capacitación en vivo por Meet, con grabación. No se promete
                fecha ni duración. */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
              <div className="flex items-start gap-2.5 bg-[#8CF0B4]/10 border border-[#8CF0B4]/30 rounded-xl px-3.5 py-3" data-testid="landing-training">
                <Users className="h-4 w-4 text-[#8CF0B4] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#efe6d2] leading-snug">
                  <span className="font-semibold text-white">Capacitación en vivo por Meet.</span>{' '}
                  Con casos reales. Si no podés ese día, te queda grabada.
                </p>
              </div>
              <div className="flex items-start gap-2.5 bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-3" data-testid="checkout-guarantee">
                <ShieldCheck className="h-4 w-4 text-[#8CF0B4] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#efe6d2] leading-snug">
                  <span className="font-semibold text-white">Garantía de 10 días.</span>{' '}
                  Si no te sirve, te devuelvo la plata.
                </p>
              </div>
            </div>

            <Button
              size="lg"
              onClick={handleActivate}
              disabled={checkoutLoading}
              data-testid="landing-buy-btn-final"
              className={`w-full mt-6 ${BTN_PRIMARY} disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100`}
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Redirigiendo…
                </>
              ) : (
                <>
                  Activar acceso
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
            <p className="text-[#a9a08c] text-xs mt-3 text-center">
              Pago seguro con Mercado Pago · Acceso inmediato por mail
            </p>
          </div>

          {/* Pack */}
          <div className="lg:order-1">
          <Papel semilla={31} giro={0.4} className="h-full flex flex-col p-6 sm:p-8" data-testid="landing-pack">
            <p className={`${FUENTE_TITULOS} text-[#14110d] text-2xl`}>¿Tenés pocos alumnos?</p>
            <p className="mt-1 text-[#4a4333] text-sm">Pack de {PACK_ENTREVISTAS} entrevistas</p>
            <p className={`${FUENTE_TITULOS} mt-3 text-5xl sm:text-6xl text-[#14110d] leading-none`}>
              {formatPriceARS(PACK_PRICE_ARS)}
            </p>
            <ul className="mt-5 space-y-2 text-sm text-[#14110d]">
              {[
                'No vencen',
                'Los retests de esas personas no gastan',
                'Si pasás al ilimitado, te descontamos lo que pagaste',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[#27AE60] flex-shrink-0 mt-0.5" strokeWidth={4} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-6">
              <Button onClick={handlePack} data-testid="landing-pack-btn" className={`w-full h-12 px-6 text-sm ${BTN_PRIMARY}`}>
                Comprar el pack de {PACK_ENTREVISTAS}
              </Button>
            </div>
          </Papel>
          </div>

          {/* Gimnasios y equipos: se cierra por WhatsApp, no hay checkout. */}
          <div
            className="lg:order-3 flex flex-col bg-white/[0.03] border-2 border-[#efe6d2]/25 rounded-3xl p-6 sm:p-8"
            data-testid="landing-price-gym"
          >
            <p className={`${FUENTE_TITULOS} text-[#efe6d2] text-2xl`}>Gimnasios y equipos</p>
            <p className="mt-1 text-[#d9cfb6] text-sm">Capacitación en vivo + EVALPRO</p>
            <p className={`${FUENTE_ROTULO} mt-4 text-[#a9a08c] text-xs`}>Desde</p>
            <p className={`${FUENTE_TITULOS} text-5xl sm:text-6xl text-[#efe6d2] leading-none`}>
              {formatPriceARS(GIMNASIO_PRECIOS[0].precio)}
            </p>
            <ul className="mt-5 space-y-2 text-sm text-[#efe6d2]">
              {[
                'Capacitación en vivo de 2 horas para tus entrenadores, con casos reales',
                'Una cuenta de EVALPRO para el gimnasio y guía de uso',
                'Todo el equipo con el mismo protocolo de entrevista inicial',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-[#8CF0B4] flex-shrink-0 mt-0.5" strokeWidth={3} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-5 pt-4 border-t border-white/10 grid gap-1.5 text-sm">
              {GIMNASIO_PRECIOS.map(({ equipo, precio }) => (
                <div key={equipo} className="flex justify-between gap-3" style={{ flexWrap: 'nowrap' }}>
                  <dt className="text-[#d9cfb6]">{equipo}</dt>
                  <dd className="text-[#efe6d2] font-semibold">{precio ? formatPriceARS(precio) : 'A cotizar'}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-auto pt-6">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsApp}
                data-testid="landing-price-gym-btn"
                className={`w-full inline-flex items-center justify-center gap-2 h-12 ${BTN_SECUNDARIO}`}
              >
                <MessageCircle className="h-4 w-4" />
                Consultar por mi equipo
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* ============ 6 · GIMNASIOS (#gimnasios) ============ */}
      {/* Destino de los anuncios B2B. Va después del precio: el que llegó
          hasta acá y pensó "esto lo quiero para todo mi equipo" ya se
          autoseleccionó. Con más presencia que el resto: borde y brillo neón. */}
      <section
        id="gimnasios"
        className="w-full px-4 py-16 sm:py-24 scroll-mt-20"
        style={{ backgroundColor: C.navyDeep }}
        data-testid="landing-gym-section"
      >
        <div className="max-w-6xl mx-auto relative rounded-[2rem] border-2 border-[#8CF0B4]/50 bg-gradient-to-br from-[#16301f] via-[#0b0b0c] to-[#0b0b0c] shadow-[0_0_40px_rgba(140,240,180,0.10)] px-5 py-10 sm:px-10 sm:py-14 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-10 lg:gap-14 items-start">
            <div>
              <Eyebrow>Para gimnasios, centros y equipos</Eyebrow>
              <h2 className={`${FUENTE_TITULOS} text-4xl sm:text-5xl lg:text-6xl  leading-[0.92] text-white`}>
                Todo tu equipo, <Acento>con el mismo protocolo</Acento> de entrevista inicial.
              </h2>
              <p className="mt-5 text-[#d9cfb6] text-base sm:text-lg leading-relaxed max-w-xl">
                Hoy cada entrenador recibe al alumno nuevo a su manera. Con EVALPRO todos hacen la
                misma entrevista, con criterio ACSM, y cada alumno queda registrado con su informe.
              </p>

              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="landing-gym-items">
                {GIMNASIOS.map(({ Icon, titulo, desc }) => (
                  <li key={titulo} className="bg-white/[0.05] border border-white/10 rounded-2xl p-5">
                    <Icon className="h-6 w-6 text-[#8CF0B4]" />
                    <p className="mt-3 text-white font-semibold leading-snug">{titulo}</p>
                    <p className="mt-1.5 text-[#a9a08c] text-sm leading-relaxed">{desc}</p>
                  </li>
                ))}
              </ul>

              <p className="mt-8 text-[#d9cfb6] text-sm">
                Las condiciones dependen del tamaño del equipo. Escribime y lo armamos juntos.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWhatsApp}
                  data-testid="landing-gym-cta-whatsapp"
                  className={`inline-flex items-center justify-center gap-2 ${BTN_PRIMARY}`}
                >
                  <MessageCircle className="h-5 w-5" />
                  Quiero EVALPRO para mi equipo
                </a>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleInstagram}
                  data-testid="landing-gym-cta-btn"
                  className={`inline-flex items-center justify-center ${BTN_SECUNDARIO}`}
                >
                  Escribime por Instagram
                </a>
              </div>
            </div>

            {/* Prueba de equipos que ya lo usan (mismos textos aprobados). */}
            <div className="hidden lg:grid gap-4" data-testid="landing-gym-proof">
              {TESTIMONIOS.slice(0, 2).map((t) => (
                <figure key={t.nombre} className="bg-white/[0.04] border border-white/10 rounded-2xl p-6">
                  <blockquote className="text-white text-base leading-relaxed">“{t.texto}”</blockquote>
                  <figcaption className="mt-3">
                    <p className="text-white font-semibold text-sm">{t.nombre}</p>
                    <p className="text-[#a9a08c] text-xs">{t.rol}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 7 · PREGUNTAS ============ */}
      <Section tono="white" data-testid="landing-faq-section">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-8 lg:gap-12 items-start">
          <div>
            <Eyebrow>Preguntas frecuentes</Eyebrow>
            <Titulo>Lo que más me preguntan</Titulo>
            <a
              href="#gimnasios"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#8CF0B4] hover:underline underline-offset-4"
            >
              ¿Sos un gimnasio o un equipo? Mirá la propuesta
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <FAQAccordion />
        </div>
      </Section>

      {/* ============ RECURSO GRATIS (#guia) ============ */}
      {/* Era una seccion completa; queda como banda compacta. Se mantiene el
          ancla #guia porque se linkea desde Instagram. */}
      <section id="guia" className="w-full px-4 py-10 scroll-mt-20" style={{ backgroundColor: C.navyDeep }} data-testid="landing-lead-magnet-section">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left">
          <div>
            <p className={`${FUENTE_TITULOS} text-white  text-2xl`} data-testid="lead-magnet-title">
              ¿Todavía no te decidís?
            </p>
            <p className="text-[#a9a08c] text-sm mt-1" data-testid="lead-magnet-subtitle">
              Bajate gratis la entrevista inicial en una hoja: las 3 preguntas del criterio ACSM, en PDF.
            </p>
          </div>
          <Button
            onClick={() => setShowLeadModal(true)}
            data-testid="lead-magnet-cta"
            className="h-12 px-6 bg-transparent border-2 border-[#8CF0B4] hover:bg-[#8CF0B4]/10 text-[#8CF0B4] font-semibold rounded-full flex-shrink-0"
          >
            <FileText className="h-4 w-4 mr-2" />
            Descargar gratis
          </Button>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-white/5 px-4 pt-10 pb-24 md:pb-10" style={{ backgroundColor: C.navy }}>
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-6">
          <div className="flex items-center gap-6" data-testid="footer-brand-band">
            <img src="/landing/logo-evalpro-claro.png" alt="EVALPRO" className="h-10 w-auto" loading="lazy" data-testid="footer-logo-evalpro" />
            <img src="/landing/logo-efs-claro.png" alt="Ejercicio Físico & Salud" className="h-14 w-auto" loading="lazy" data-testid="footer-efs-mark" />
          </div>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#a9a08c]">
            <p>© {new Date().getFullYear()} EVALPRO · Un proyecto de Ejercicio Físico &amp; Salud</p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <button onClick={() => navigate('/login')} className="hover:text-white transition-colors" data-testid="footer-login-btn">
                Iniciar sesión
              </button>
              <span aria-hidden="true">·</span>
              <button onClick={handleActivate} className="hover:text-white transition-colors" data-testid="footer-buy-btn">
                Comprar
              </button>
              <span aria-hidden="true">·</span>
              <button onClick={() => navigate('/terminos')} className="hover:text-white transition-colors" data-testid="footer-terms-btn">
                Términos y Condiciones
              </button>
              <span aria-hidden="true">·</span>
              <button onClick={() => navigate('/privacidad')} className="hover:text-white transition-colors" data-testid="footer-privacy-btn">
                Política de Privacidad
              </button>
            </div>
          </div>
        </div>
      </footer>

      <MobileStickyCTA onActivate={handleActivate} cuota={cuota} />

      {/* Lead magnet: captura de mail + descarga del PDF + envio por Resend */}
      <LeadModal open={showLeadModal} onClose={() => setShowLeadModal(false)} />
    </div>
  );
}

function MobileStickyCTA({ onActivate, cuota }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > window.innerHeight * 1.2);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={`md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 bg-gradient-to-t from-[#0b0b0c] via-[#0b0b0c]/95 to-transparent transition-all duration-300 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
      }`}
      data-testid="mobile-sticky-cta"
      aria-hidden={!visible}
    >
      <Button
        onClick={onActivate}
        tabIndex={visible ? 0 : -1}
        data-testid="mobile-sticky-buy-btn"
        className={`w-full h-12 text-sm ${BTN_PRIMARY} active:scale-95`}
      >
        Activar acceso
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
      <p className="text-center text-[11px] text-[#d9cfb6] mt-1.5">
        3 cuotas sin interés de {cuota} · Garantía 10 días
      </p>
    </div>
  );
}
